import { Link } from '@tanstack/react-router';
import { AiSurface } from '@/components/ui/ai-surface';
import { Spinner } from '@/components/ui/spinner';
import type { EngagementActivity } from '@/lib/api/engagement-activity';

/**
 * Thin status strip above every engagement screen. Appears only while
 * something is happening:
 *
 *   - documents are being read (parsed / indexed), or
 *   - an AI run is in flight, or
 *   - proposals are waiting in the review queue.
 *
 * It is the visible half of live refresh (`useEngagementLiveRefresh`):
 * the screens update on their own, and this strip says why and points at
 * the one place a human decision is needed. Renders nothing when the
 * engagement is quiet, so it costs no vertical space day to day.
 *
 * Uses the `AiSurface` primitive (lavender AI treatment) because the
 * content is about AI work in progress; no styles are inlined here.
 */

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function EngagementActivityStrip({
  engagementId,
  activity,
  onReviewQueue = false,
  reviewQueueAvailable = true,
}: {
  engagementId: string;
  activity: EngagementActivity | undefined;
  /** Already on the review queue: an "open review" pointer would link to this page. */
  onReviewQueue?: boolean;
  /**
   * The engagement has the IV&V review queue (any IV&V module). Without
   * one the queue's route redirects to Discovery or Selection, so the
   * pointer to it is not shown; `pending_proposals` counts that queue only.
   */
  reviewQueueAvailable?: boolean;
}) {
  if (!activity) return null;
  const { documents, ai_runs: aiRuns } = activity.processing;
  const pending = onReviewQueue || !reviewQueueAvailable ? 0 : activity.pending_proposals;

  const working = documents + aiRuns > 0;
  if (!working && pending === 0) return null;

  const parts: string[] = [];
  if (documents > 0) parts.push(`Reading ${plural(documents, 'document', 'documents')}`);
  if (aiRuns > 0) parts.push(`${plural(aiRuns, 'AI analysis', 'AI analyses')} running`);

  return (
    <AiSurface
      role="status"
      aria-live="polite"
      className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card px-3 py-2 text-body-sm"
    >
      {working ? (
        <span className="inline-flex items-center gap-2">
          <Spinner />
          <span className="font-medium">
            {parts.join(' · ')}. This page updates as results arrive.
          </span>
        </span>
      ) : null}
      {pending > 0 ? (
        <span className="inline-flex items-center gap-2">
          <span>
            {plural(pending, 'AI proposal is', 'AI proposals are')} waiting for your review.
          </span>
          <Link
            to="/engagements/$engagementId/proposals"
            params={{ engagementId }}
            className="font-medium underline decoration-ai-ink/40 underline-offset-2 hover:decoration-ai-ink"
          >
            Open review →
          </Link>
        </span>
      ) : null}
    </AiSurface>
  );
}
